/* eslint-disable */
import React, { FC, useEffect, useLayoutEffect } from 'react';
import { Button, Divider, Typography } from 'antd';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import LeftLayout from '~/layouts/LeftLayout';
import WizardContent from '~/layouts/WizardContent';
import ApplicationNavigation from '~/layouts/uae/ApplicationNavigation';
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  ClockCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useIsMobile } from '~/hooks/useScreenSizes';
import { useDirection } from '~/hooks/i18nHooks';
import { usePageTitle } from '~/context/PageTitleContext';
import { getTrackingId } from '~/utils/handleGetUtm';
import { useGetApplication } from '~/controllers/wizard';
import { CopySvg } from '~/img';

const MerchantOnboardingInProgress: FC = () => {
  const { t } = useTranslation('ksaApplication');
  const isMobile = useIsMobile();
  const dir = useDirection();
  const applicationQuery: any = useGetApplication();
  const applicationDisplayId = applicationQuery?.data?.applicationDisplayId;
  const { setPageTitle } = usePageTitle();

  const servicesPayload = (applicationQuery?.data?.payload?.services || {}) as Record<string, { enabled?: boolean }>;
  const isPosEnabled = !!(servicesPayload?.POS?.enabled);
  const isEcomEnabled = !!(servicesPayload?.eCommerce?.enabled);
  // POS-only: hide "Go to Portal" (portal is ECOM-related)
  const showGoToPortal = isEcomEnabled;

  useLayoutEffect(() => {
    setPageTitle(t('Application Approved'));
    return () => setPageTitle('');
  }, []);

  useEffect(() => {
    (window as any).dataLayer.push({
      event: 'event_congratulations',
      page_ln: 'en',
      page_name: window.location.href,
      page_source: document.referrer || 'direct',
      page_section: 'Congratulations',
      page_title: t('Application Approved'),
      trackingId: getTrackingId(applicationQuery?.data),
    });
  }, []);

  return (
    <WizardContent
      content={
        <>
          {!isMobile && (
            <Container dir={dir}>
              <div className="desktop-card">
                <div className="SuccessfulOnboarding-Img">
                  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <path d="M6 19.5L13.5 27L30 9" stroke="white" stroke-width="3.375" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <Typography.Paragraph className="subTitle1">
                  {t('You have been approved!')}
                </Typography.Paragraph>

                <div className="app-number-section" dir="ltr">
                  <p className="app-number-label">{t('Your application number.')}</p>
                  <Typography.Paragraph
                    className="app-number-value"
                    dir="ltr"
                    copyable={{
                      text: applicationDisplayId,
                      icon: [
                        <CopyButton key="copy">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M13.4735 6.66699C14.5842 6.66699 15.1396 6.66699 15.5959 6.78635C16.8783 7.12181 17.8798 8.12332 18.2153 9.40574C18.3346 9.86203 18.3346 10.4174 18.3346 11.5281V12.5003C18.3346 14.5224 18.3346 15.5334 17.9468 16.3083C17.5922 17.0167 17.0177 17.5912 16.3092 17.9458C15.5344 18.3337 14.5234 18.3337 12.5013 18.3337H11.5291C10.4184 18.3337 9.863 18.3337 9.40672 18.2143C8.1243 17.8788 7.12278 16.8773 6.78732 15.5949C6.66797 15.1386 6.66797 14.5833 6.66797 13.4725M7.5013 13.3337C5.47922 13.3337 4.46818 13.3337 3.69337 12.9458C2.98494 12.5912 2.41042 12.0167 2.05581 11.3083C1.66797 10.5334 1.66797 9.52241 1.66797 7.50033C1.66797 5.47824 1.66797 4.4672 2.05581 3.6924C2.41042 2.98397 2.98494 2.40945 3.69337 2.05483C4.46818 1.66699 5.47922 1.66699 7.5013 1.66699C9.52338 1.66699 10.5344 1.66699 11.3092 2.05483C12.0177 2.40945 12.5922 2.98397 12.9468 3.6924C13.3346 4.4672 13.3346 5.47824 13.3346 7.50033C13.3346 9.52241 13.3346 10.5334 12.9468 11.3083C12.5922 12.0167 12.0177 12.5912 11.3092 12.9458C10.5344 13.3337 9.52338 13.3337 7.5013 13.3337Z" stroke="#0069B1" stroke-width="1.375" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          <span className="copy-label">{t('Copy')}</span>
                        </CopyButton>,
                        <CheckCircleOutlined key="copied" style={{ color: '#21A850' }} />,
                      ],
                    }}
                  >
                    {applicationDisplayId}
                  </Typography.Paragraph>
                </div>

                <Divider style={{ margin: '0px 0' }} />

                <div className="info-rows">
                  <div className="info-row" dir={dir}>
                    <ClockCircleOutlined className="info-icon" />
                    <span>{t('What Happens Next')}</span>
                  </div>
                  <div className="info-row" dir={dir}>
                    <QuestionCircleOutlined className="info-icon" />
                    <span>
                      {t('Have any questions? Feel free to call us at ')}{' '}
                      <a href="tel:920017697" className="phone-link">920017697</a>.
                    </span>
                  </div>
                </div>

                {showGoToPortal && (
                  <Button
                    onClick={() =>
                      window.open(
                        'https://portal.ksa.ngenius-payments.com/?tenant=NIARABIA',
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                    className="btnHelp"
                    type="primary"
                    id="portal"
                  >
                    {t('Go to Portal')}
                  </Button>
                )}
              </div>
            </Container>
          )}

          {isMobile && (
            <Container dir={dir}>
              <div>
                <div className="SuccessfulOnboarding-Img">
                  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <path d="M6 19.5L13.5 27L30 9" stroke="white" stroke-width="3.375" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <Typography.Paragraph className="subTitle1">
                  {t('You have been approved!')}
                </Typography.Paragraph>
                <ApplicationNumberSection dir="ltr">
                  <ApplicationNumberLabel>{t('Your application number.')}</ApplicationNumberLabel>
                  <ApplicationNumberWrapper dir="ltr">
                    <Typography.Paragraph
                      className="identifier"
                      dir="ltr"
                      copyable={{
                        text: applicationDisplayId,
                        icon: [
                          <CopyButton key="copy">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                              <path d="M13.4735 6.66699C14.5842 6.66699 15.1396 6.66699 15.5959 6.78635C16.8783 7.12181 17.8798 8.12332 18.2153 9.40574C18.3346 9.86203 18.3346 10.4174 18.3346 11.5281V12.5003C18.3346 14.5224 18.3346 15.5334 17.9468 16.3083C17.5922 17.0167 17.0177 17.5912 16.3092 17.9458C15.5344 18.3337 14.5234 18.3337 12.5013 18.3337H11.5291C10.4184 18.3337 9.863 18.3337 9.40672 18.2143C8.1243 17.8788 7.12278 16.8773 6.78732 15.5949C6.66797 15.1386 6.66797 14.5833 6.66797 13.4725M7.5013 13.3337C5.47922 13.3337 4.46818 13.3337 3.69337 12.9458C2.98494 12.5912 2.41042 12.0167 2.05581 11.3083C1.66797 10.5334 1.66797 9.52241 1.66797 7.50033C1.66797 5.47824 1.66797 4.4672 2.05581 3.6924C2.41042 2.98397 2.98494 2.40945 3.69337 2.05483C4.46818 1.66699 5.47922 1.66699 7.5013 1.66699C9.52338 1.66699 10.5344 1.66699 11.3092 2.05483C12.0177 2.40945 12.5922 2.98397 12.9468 3.6924C13.3346 4.4672 13.3346 5.47824 13.3346 7.50033C13.3346 9.52241 13.3346 10.5334 12.9468 11.3083C12.5922 12.0167 12.0177 12.5912 11.3092 12.9458C10.5344 13.3337 9.52338 13.3337 7.5013 13.3337Z" stroke="#0069B1" stroke-width="1.375" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                            <span className="copy-label">{t('Copy')}</span>
                          </CopyButton>,
                          <CheckCircleOutlined key="copied" style={{ color: '#21A850' }} />,
                        ],
                      }}
                      style={{ marginBottom: 0 }}
                    >
                      {applicationDisplayId}
                    </Typography.Paragraph>
                  </ApplicationNumberWrapper>
                </ApplicationNumberSection>
                <Divider />
                <div className="nextStepsSection">
                  <Typography.Title className="sbTitleMobile" level={4}>
                    <ClockCircleOutlined className="nextStepsIcon" />
                    {t('What Happens Next')}
                  </Typography.Title>
                  <ul>
                    <li>
                      <Typography.Paragraph>
                        {t(
                          'Instructions for setting up your selected product/s have been emailed to you.'
                        )}
                      </Typography.Paragraph>
                    </li>
                    <li>
                      <Typography.Paragraph>
                        {t(
                          'If we need any additional info, we will contact you within 2 business days.'
                        )}
                      </Typography.Paragraph>
                    </li>
                  </ul>
                  <div>
                    <Typography.Paragraph className="sbTitleMobile questionRow">
                      <QuestionCircleOutlined className="nextStepsIcon" />

                      {t('Have any questions? Feel free to call us at ')}
                      {t('800-4448.')}
                    </Typography.Paragraph>
                  </div>
                </div>
              </div>
              {showGoToPortal && (
                <Button
                  onClick={() =>
                    window.open(
                      'https://portal.ksa.ngenius-payments.com/?tenant=NIARABIA',
                      // 'https://identity.sandbox.ksa.ngenius-payments.com/auth/realms/NIARABIA/protocol/openid-connect/auth?client_id=gateway-portal&response_type=code&redirect_uri=https%3A%2F%2Fportal.sandbox.ksa.ngenius-payments.com%2Fapi%2Fauth%2Fopen-id%3Ftenant%3DNIARABIA',
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }
                  style={{ marginTop: 60, width: '100%' }}
                  className="btnHelp"
                  type="primary"
                  id="portal"
                >
                  <Typography.Paragraph
                    style={{ color: 'white', paddingLeft: 40, paddingRight: 40, fontWeight: 600 }}
                  >
                    {t('Go to Portal')}
                  </Typography.Paragraph>
                </Button>
              )}
            </Container>
          )}
        </>
      }
      left={
        <LeftLayout>
          <ApplicationNavigation />
        </LeftLayout>
      }
      right={<></>}
    />
  );
};

const ApplicationNumberSection = styled.div`
  margin-top: 24px;
  margin-left: -60px;
  margin-right: -60px;
  text-align: center;
  padding: 24px 60px;
  border-top: 1px solid #e5e7eb;
  direction: ltr !important;
`;

const ApplicationNumberLabel = styled.p`
  color: #6b7280;
  font-family: Inter, sans-serif;
  font-size: 14px;
  margin-bottom: 8px;
`;
const CopyButton = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #005996;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; 
  letter-spacing: 0.14px;
  cursor: pointer;
  direction: ltr !important;
`;

const ApplicationNumberWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  direction: ltr !important;

  .identifier,
  .identifier.ant-typography-rtl {
    color: #070707;
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-weight: 600;
    direction: ltr !important;
    display: inline-flex;
    align-items: center;

    .ant-typography-copy {
      margin-left: 8px !important;
      margin-right: 0 !important;
    }
  }

  .ant-typography-copy {
    margin-left: 8px !important;
    margin-right: 0 !important;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column; /* or row based on layout, column works well here */
  justify-content: center;
  align-items: center;
  /* Ensure container has enough height */
  min-height: 60vh; /* or some fixed height / parent height */

  text-align: center;
  .sbTitleMobile {
    color: #4a4a4a;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    margin:0px !important;
  }

  .SuccessfulOnboarding-Img {
    display: flex;
    width: 72px;
    height: 72px;
    padding: 18px;
    align-items: flex-start;
    gap: 10px;
    margin-top: 86px;
    margin-left: auto;
    margin-right: auto;
    border-radius: 50%;
    background-color: #2fbf71;
    box-sizing: border-box;

    @media (max-width: 768px) {
      margin-top: 40px;
    }
  }
  .subTitle1 {
    color: #218850;
    text-align: center;

    /* Heading 3/Medium */
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 24px;
    font-style: normal;
    font-weight: 500;
    line-height: 40px; /* 166.667% */
    margin:0px ! important;
  }

  .subTitle2 {
    color: #4a4a4a;
    text-align: center;

    /* LG/Medium */
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px; /* 150% */
  }

  .desktop-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 420px;
    padding: 0 24px;
  gap:24px;

  }

  .app-number-section {
    width: 100%;
    text-align: center;
    padding: 12px 0 8px;
    direction: ltr !important;
  }

  .app-number-label {
    color: #6b7280;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    margin-bottom: 6px;
  }

  .app-number-value,
  .app-number-value.ant-typography-rtl {
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: #070707;
    margin-bottom: 0 !important;
    display: inline-flex;
    align-items: center;
    direction: ltr !important; /* keep ID always LTR */

    .ant-typography-copy {
      margin-left: 8px !important;
      margin-right: 0 !important;
    }
  }

  .info-rows {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-align: start;
    margin-bottom: 8px;
  }

  .info-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    color: #4a4a4a;
    font-weight: 500;
  }

  .info-icon {
    color: #0069b1;
    font-size: 20px;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .phone-link {
    color: #0069b1;
    font-weight: 600;
    text-decoration: none;
  }

  .btnHelp {
    border-radius: 12px !important;
    height: 48px !important;
    font-size: 15px;
    font-weight: 600;
    margin-top: 8px;
    width: 100%;
    max-width: 360px;
  }

  .nextStepsSection {
    text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
    direction: ${({ dir }) => (dir === 'rtl' ? 'rtl' : 'ltr')};
    width: 100%;

    .nextStepsIcon {
      color: #0069b1;
      font-size: 20px;
      margin-inline-end: 12px;
    }

    .questionRow {
      margin-top: 16px !important;
    }

    ul {
      padding-inline-start: 24px;
      margin-top: 8px;
      margin-bottom: 8px;
      text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};

      li {
        margin-bottom: 8px;

        .ant-typography {
          margin-bottom: 0;
          font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
        }
      }
    }
  }
`;


export default MerchantOnboardingInProgress;
